import { useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Player, PlayerEvent } from "@lottiefiles/react-lottie-player"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarDay, faLocationDot, faSackDollar } from "@fortawesome/pro-regular-svg-icons"
import { Box, Link, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { getTakeawayReportsRootForPath } from "../../../../../constants/routes"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import type { IMyOrderDetails } from "../../../../payments/orderTypes"
import type { MonetaryAmount } from "../../../../currency/currencyTypes"
import { ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import loadingAnimationSuccess from "../../../../../shared/components/createOrder/components/assets/success.json"
import type {
    TakeawayDeliveryLocation,
    TakeawayKitchen,
} from "../../../../../shared/components/createOrder/components/takeaway/takeawayTypes"

import { TakeawayResultCard } from "./TakeawayResultCard"

type PickupInfo = Readonly<{
    name: string
    timeInterval?: Readonly<{ start: string; end: string }>
}>

function resolvePickupInfo(
    selectedLocation: TakeawayDeliveryLocation | null | undefined,
    deliveryLocationFromOrder: PickupInfo | undefined,
): PickupInfo | undefined {
    if (selectedLocation) {
        const { displayName: name, timeInterval } = selectedLocation
        return { name, timeInterval }
    }
    return deliveryLocationFromOrder
}

type PickupInfoValueProps = Readonly<{
    pickupInfo: PickupInfo | undefined
    openingHours: string | undefined
}>

function PickupInfoValue({ pickupInfo, openingHours }: PickupInfoValueProps) {
    const { classes } = useStyles()
    const dt = useDateTime()
    if (pickupInfo) {
        const { name, timeInterval } = pickupInfo
        if (timeInterval) {
            const formatted = dt.formatTimeInterval(timeInterval)
            if (!formatted) return <Typography className={classes.infoValueBold}>{name}</Typography>
            const { start, end } = formatted
            return (
                <LocalizedStrict id="takeaway-order-confirmed-dialog-pickup-location-value" vars={{ name, start, end }}>
                    <Typography className={classes.infoValueBold}>
                        {name} between {start} and {end}
                    </Typography>
                </LocalizedStrict>
            )
        }
        return <Typography className={classes.infoValueBold}>{name}</Typography>
    }
    return <Typography className={classes.infoValueBold}>{openingHours}</Typography>
}

type Props = Readonly<{
    orders: IMyOrderDetails[] | undefined
    kitchen?: TakeawayKitchen
    totalPrice?: MonetaryAmount
    selectedLocation?: TakeawayDeliveryLocation | null
    isPublic?: boolean
}>

const useStyles = makeStyles()((theme) => ({
    animation: {
        height: "4rem",
    },
    textBlock: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: ".25rem",
    },
    title: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.33,
        color: theme.palette.tailwind.slate[950],
        textAlign: "center",
    },
    caption: {
        fontWeight: 400,
        fontSize: ".875rem",
        lineHeight: 1.43,
        color: theme.palette.tailwind.slate[950],
        textAlign: "center",
    },
    infoBox: {
        width: "100%",
        background: theme.palette.tailwind.blue[50],
        borderRadius: ".5rem",
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    infoRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
        justifyContent: "space-between",
    },
    infoLeft: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        flexShrink: 0,
    },
    infoIconWrapper: {
        width: "1.5rem",
        height: "1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    infoIcon: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "1.125rem",
    },
    infoText: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.5,
        color: theme.palette.tailwind.slate[900],
        flexShrink: 0,
    },
    infoValueBold: {
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: 1.5,
        color: theme.palette.tailwind.slate[900],
        textAlign: "right",
    },
    receiptLink: {
        display: "block",
        textAlign: "center",
        fontSize: ".875rem",
        fontWeight: 500,
        marginTop: ".25rem",
        cursor: "pointer",
    },
    dashboardButton: {
        width: "100%",
        marginTop: ".5rem",
    },
}))

export function TakeawayOrderConfirmed({ orders, kitchen, totalPrice, selectedLocation, isPublic = false }: Props) {
    const { classes } = useStyles()
    const playerRef = useRef<Player>(null)
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const [showReceipt, setShowReceipt] = useState(false)

    const handleGoToDashboard = () => navigate(getTakeawayReportsRootForPath(pathname))

    const firstOrder = orders?.[0]

    // Pass orderId as string to prevent Fluent from adding thousand separators (e.g. 169,507)
    const orderId = String(firstOrder?.id ?? "")

    const deliveryLocationFromOrder = firstOrder?.deliveries?.[0]?.deliveryLocation
    const pickupInfo = resolvePickupInfo(selectedLocation, deliveryLocationFromOrder)

    const deliveryDates = (orders ?? [])
        .flatMap((o) => o.deliveries ?? [])
        .map((d) => (d.deliveryTime ? dateTimeFactory.fromApi(d.deliveryTime) : undefined))
        .filter((dt): dt is NonNullable<typeof dt> => dt !== undefined)

    const totalAmount = toDineroOrUndefined(totalPrice)
    const orderCount = orders?.length ?? 0
    const isMultipleDeliveries = deliveryDates.length > 1

    const openingHours = kitchen?.brandingDetails?.openingHours
    const showInfoBox = openingHours || pickupInfo?.name || deliveryDates.length > 0 || totalAmount

    return (
        <>
            <TakeawayResultCard>
                {firstOrder && (
                    <Player
                        ref={playerRef}
                        src={loadingAnimationSuccess}
                        autoplay
                        speed={1}
                        keepLastFrame
                        className={classes.animation}
                        onEvent={(event) => {
                            if (event === PlayerEvent.Complete) playerRef.current?.pause()
                        }}
                    />
                )}

                <Stack className={classes.textBlock}>
                    {firstOrder && (
                        <LocalizedStrict id="takeaway-order-confirmed-dialog-title">
                            <Typography className={classes.title}>Order completed!</Typography>
                        </LocalizedStrict>
                    )}
                    {firstOrder && !isMultipleDeliveries && (
                        <LocalizedStrict id="takeaway-order-confirmed-dialog-order-id" vars={{ orderId }}>
                            <Typography className={classes.caption}>Order ID: {orderId}</Typography>
                        </LocalizedStrict>
                    )}
                </Stack>

                {showInfoBox && (
                    <Box className={classes.infoBox}>
                        {(pickupInfo?.name || openingHours) && (
                            <Box className={classes.infoRow}>
                                <Box className={classes.infoLeft}>
                                    <Box className={classes.infoIconWrapper}>
                                        <FontAwesomeIcon icon={faLocationDot} className={classes.infoIcon} />
                                    </Box>
                                    <LocalizedStrict id="takeaway-order-confirmed-dialog-pickup-hours-label">
                                        <Typography className={classes.infoText}>Pickup:</Typography>
                                    </LocalizedStrict>
                                </Box>
                                <PickupInfoValue pickupInfo={pickupInfo} openingHours={openingHours} />
                            </Box>
                        )}
                        {!isMultipleDeliveries &&
                            deliveryDates.map((dt, index) => (
                                <Box key={index} className={classes.infoRow}>
                                    <Box className={classes.infoLeft}>
                                        <Box className={classes.infoIconWrapper}>
                                            <FontAwesomeIcon icon={faCalendarDay} className={classes.infoIcon} />
                                        </Box>
                                        <LocalizedStrict id="takeaway-order-confirmed-dialog-delivery-date-label">
                                            <Typography className={classes.infoText}>Delivery date:</Typography>
                                        </LocalizedStrict>
                                    </Box>
                                    <Typography className={classes.infoValueBold}>
                                        {dateTimeFactory.formatCustom(dt, "MMMM d, yyyy")}
                                    </Typography>
                                </Box>
                            ))}
                        {totalAmount && (
                            <Box className={classes.infoRow}>
                                <Box className={classes.infoLeft}>
                                    <Box className={classes.infoIconWrapper}>
                                        <FontAwesomeIcon icon={faSackDollar} className={classes.infoIcon} />
                                    </Box>
                                    <LocalizedStrict
                                        id={
                                            isMultipleDeliveries
                                                ? "takeaway-order-confirmed-dialog-total-amount-label"
                                                : "takeaway-order-confirmed-dialog-amount-label"
                                        }
                                    >
                                        <Typography className={classes.infoText}>
                                            {isMultipleDeliveries ? "Total amount:" : "Amount:"}
                                        </Typography>
                                    </LocalizedStrict>
                                </Box>
                                <Typography className={classes.infoValueBold}>
                                    {moneyFactory.format(totalAmount)}
                                </Typography>
                            </Box>
                        )}
                        {firstOrder && (
                            <LocalizedStrict
                                id={
                                    isMultipleDeliveries
                                        ? "takeaway-order-confirmed-dialog-show-receipts-button"
                                        : "takeaway-order-confirmed-dialog-show-receipt-button"
                                }
                                vars={isMultipleDeliveries ? { count: String(orderCount) } : undefined}
                            >
                                <Link
                                    component="button"
                                    className={classes.receiptLink}
                                    onClick={() => setShowReceipt(true)}
                                >
                                    {isMultipleDeliveries ? `Show ${orderCount} Receipts` : "Show Receipt"}
                                </Link>
                            </LocalizedStrict>
                        )}
                    </Box>
                )}

                {!isPublic && (
                    <LocalizedStrict id="takeaway-order-confirmed-dialog-go-to-dashboard-button">
                        <StandardButton
                            variant="outlined"
                            className={classes.dashboardButton}
                            onClick={handleGoToDashboard}
                        >
                            Go to Dashboard
                        </StandardButton>
                    </LocalizedStrict>
                )}
            </TakeawayResultCard>

            {firstOrder && showReceipt && (
                <ReceiptDialog
                    receipt={{ orderId: firstOrder.id, orderUid: firstOrder.uid, orderType: firstOrder.orderType }}
                    orders={orders ?? []}
                    open={showReceipt}
                    onClose={() => setShowReceipt(false)}
                    isPublic={isPublic}
                />
            )}
        </>
    )
}
